/**
 * @file seller.ts
 * @module api/apiJSON/seller
 * @description API JSON payload builders for seller endpoints.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.1.1
 */

import {
  IKycStatusUpdate,
  IListSellerKyc,
  IProfileUpdate,
  IListSeller,
  IUpdateSellerState,
  IAddSeller,
  IListPayoutProfiles,
  IListPayoutOrders,
  IListPayout,
  IPayout,
} from "../../types";
export const sellerJSON = {
  listSellerKyc: ({
    pageNo,
    limit,
    sortKey,
    sortOrder,
    listType,
    searchTerm,
    warehouse,
  }: IListSellerKyc) => {
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: sortKey,
      sortOrder: sortOrder,
      needCount: true,
      listType: listType,
      searchTerm: searchTerm,
    };
    if (warehouse) {
      params.warehouse = warehouse;
    }
    return params;
  },
  sellerProfileUpdate: ({ profile, pendingCertificates }: IProfileUpdate) => {
    let form = new FormData();
    form.append("ownerName", profile.ownerName);
    form.append("name", profile.name);
    form.append("phone", profile.phone);
    form.append("phoneCountry", profile.phoneCountry);
    form.append("email", profile.email);
    form.append("address", profile.address);
    form.append("lat", profile.lat.toString());
    form.append("lng", profile.lng.toString());
    form.append("about", profile.about);
    if (profile.imageReader && profile.imageReader !== "")
      form.append("image", profile.imageReader);
    if (profile.categories.length) {
      profile.categories.map((catVal: any, index: number) => {
        return form.append("categories[" + index + "]", catVal.reference._id);
      });
    }
    if (profile.certificates.length) {
      let changedCertificate: any = [];
      profile.certificates.map((catVal: any, index: number) => {
        if (catVal.isChanged === true) {
          changedCertificate.push(catVal);
        }
      });
      if (changedCertificate.length) {
        changedCertificate.map((val: any, index: number) => {
          form.append(
            "certificates[" + index + "][reference]",
            val.reference._id
          );
          form.append(
            "certificates[" + index + "][expiry]",
            val.expiry ? val.expiry : ""
          );
          if (val.imageReader) {
            form.append("certificates[" + index + "][file]", val.imageReader);
            form.append("certificates[" + index + "][removeFile]", "true");
          } else {
            form.append("certificates[" + index + "][removeFile]", "false");
          }
          return form;
        });
      }
    }
    if (profile.types.length) {
      profile.types.map((businessVal: any, index: number) => {
        return form.append("types[" + index + "]", businessVal.reference._id);
      });
    }
    return form;
  },
  kycStatusUpdate: ({ status, message }: IKycStatusUpdate) => {
    return { status: status, message: message };
  },
  listSeller: ({
    limit,
    pageNo,
    sortKey,
    sortOrder,
    searchTerm,
    status,
  }: IListSeller) => {
    return {
      limit: limit,
      pageNo: pageNo,
      sortKey: sortKey,
      sortOrder: sortOrder,
      needCount: true,
      searchTerm: searchTerm,
      status: status,
    };
  },
  updateSellerState: ({ activate }: IUpdateSellerState) => {
    return { activate: activate };
  },
  addVendorProfile: ({
    ownerName,
    name,
    phone,
    phoneCountry,
    tinNumber,
    vatNumber,
    legalName,
    tradeName,
    email,
    lat,
    lng,
    about,
    categories,
    imageReader,
    removeImage,
    paymentTerms,
    paymentMode,
    creditLimit,
    leadDuration,
    workingDays,
    currency,
    taxDetails,
    hasAnnualContract,
    contractValidFrom,
    contractValidTo,
    addressDetails,
    certificates,
    types,
    banks,
  }: any) => {
    const form = new FormData();

    // Basic details
    form.append("ownerName", ownerName);
    form.append("name", name);
    form.append("phone", phone);
    form.append("phoneCountry", phoneCountry);
    form.append("email", email);
    form.append("about", about || "");
    form.append("lat", String(lat));
    form.append("lng", String(lng));

    form.append("tinNumber", tinNumber || "");
    form.append("vatNumber", vatNumber || "");
    form.append("legalName", legalName || "");
    form.append("tradeName", tradeName || "");

    // Address object
    form.append("address[addressLine1]", addressDetails?.addressLine1 || "");
    form.append("address[city]", addressDetails?.city || "");
    form.append("address[state]", addressDetails?.state || "");
    form.append("address[countryName]", addressDetails?.countryName || "");
    form.append("address[postalCode]", addressDetails?.postalCode || "");

    // Image
    if (imageReader) {
      form.append("image", imageReader);
    }
    if (removeImage) {
      form.append("removeImage", "true");
    }

    // Categories
    if (categories?.length) {
      categories.forEach((catId: string, index: number) => {
        form.append(`categories[${index}]`, catId);
      });
    }

    // Payment details
    form.append("paymentTerms", String(paymentTerms ?? ""));
    form.append("paymentMode", String(paymentMode ?? ""));
    form.append("creditLimit", String(creditLimit ?? ""));
    form.append("leadDuration", String(leadDuration || ""));
    form.append("currency", currency || "");

    // Working days
    if (workingDays && Array.isArray(workingDays)) {
      workingDays.forEach((day: number, index: number) => {
        form.append(`workingDays[${index}]`, String(day));
      });
    }

    // Tax details
    if (taxDetails) {
      form.append("taxDetails[type]", String(taxDetails.type || ""));
      form.append("taxDetails[number]", taxDetails.number || "");
    }

    // Contract details — backend field is annualContract
    form.append("annualContract", String(hasAnnualContract || false));
    form.append("contractValidFrom", contractValidFrom || "");
    form.append("contractValidTo", contractValidTo || "");

    // Certificates
    if (certificates?.length) {
      certificates.forEach((cert: any, index: number) => {
        if (cert.reference) {
          form.append(`certificates[${index}][reference]`, cert.reference);
        }

        if (cert.expiry) {
          form.append(`certificates[${index}][expiry]`, cert.expiry);
        }

        if (cert.imageReader) {
          form.append(`certificates[${index}][file]`, cert.imageReader);
          form.append(`certificates[${index}][removeFile]`, "true");
        } else {
          form.append(`certificates[${index}][removeFile]`, "false");
        }
      });
    }

    if (types) {
      form.append("types[0]", types);
    }

    // Banks (multiple)
    if (banks?.length) {
      banks.forEach((bank: any, index: number) => {
        if (bank.tigoNumber) {
          form.append(`banks[${index}][tigoNumber]`, bank.tigoNumber);
        }

        if (bank.bankName) {
          form.append(`banks[${index}][bankName]`, bank.bankName);
        }

        if (bank.branchName) {
          form.append(`banks[${index}][branchName]`, bank.branchName);
        }

        if (bank.bankType) {
          form.append(`banks[${index}][bankType]`, bank.bankType);
        }

        if (bank.accountNumber) {
          form.append(`banks[${index}][accountNumber]`, bank.accountNumber);
        }

        if (bank.identifierCode) {
          form.append(`banks[${index}][identifierCode]`, bank.identifierCode);
        }
      });
    }

    return form;
  },
  addSellerProfile: ({
    about,
    address,
    categories,
    email,
    name,
    ownerName,
    phone,
    phoneCountry,
    types,
    certificates,
    lat,
    lng,
    imageReader,
  }: IAddSeller) => {
    let form = new FormData();
    form.append("ownerName", ownerName);
    form.append("name", name);
    form.append("phone", phone);
    form.append("phoneCountry", phoneCountry);
    form.append("email", email);
    form.append("address", address);
    form.append("lat", lat);
    form.append("lng", lng);
    form.append("about", about);
    if (imageReader && imageReader !== "") form.append("image", imageReader);
    if (categories.length) {
      categories.map((catVal: any, index: number) => {
        return form.append("categories[" + index + "]", catVal);
      });
    }
    if (certificates.length) {
      certificates.map((val: any, index: number) => {
        if (val.reference) {
          form.append("certificates[" + index + "][reference]", val.reference);
          form.append(
            "certificates[" + index + "][expiry]",
            val.expiry ? val.expiry : ""
          );
          form.append("certificates[" + index + "][file]", val.imageReader);
          form.append("certificates[" + index + "][removeFile]", "true");
          return form;
        }
      });
    }
    form.append("types[" + 0 + "]", types);
    return form;
  },
  listPayoutProfiles: ({
    limit,
    pageNo,
    sortKey,
    sortOrder,
    searchTerm,
    warehouse,
  }: IListPayoutProfiles) => {
    let params: any = {
      limit: limit,
      pageNo: pageNo,
      sortKey: sortKey,
      sortOrder: sortOrder,
      needCount: true,
      searchTerm: searchTerm,
    };
    if (warehouse) {
      params.warehouse = warehouse;
    }
    return params;
  },
  listPayoutOrders: ({
    limit,
    pageNo,
    sortKey,
    sortOrder,
    searchTerm,
    state,
    warehouse,
  }: IListPayoutOrders) => {
    let params: any = {
      limit: limit,
      pageNo: pageNo,
      sortKey: sortKey,
      sortOrder: sortOrder,
      needCount: true,
      searchTerm: searchTerm,
      state: state,
    };
    if (warehouse) {
      params.warehouse = warehouse;
    }
    return params;
  },
  listPayout: ({
    limit,
    pageNo,
    sortKey,
    sortOrder,
    searchTerm,
    warehouse,
  }: IListPayout) => {
    let params: any = {
      limit: limit,
      pageNo: pageNo,
      sortKey: sortKey,
      sortOrder: sortOrder,
      needCount: true,
      searchTerm: searchTerm,
    };
    if (warehouse) {
      params.warehouse = warehouse;
    }
  },
  payout: ({
    orders,
    paymentMethod,
    password,
    paymentRef,
    attachments,
    bankDetails,
  }: any) => {
    const form = new FormData();
    form.append("paymentMethod", paymentMethod);
    form.append("password", password);
    form.append("paymentRef", paymentRef);

    if (orders && orders.length > 0) {
      orders.forEach((orderId: string, index: number) => {
        form.append(`orders[${index}]`, orderId);
      });
    }

    if (attachments && attachments.length > 0) {
      attachments.forEach((file: any) => {
        form.append("file", file);
      });
    }

    if (bankDetails) {
      form.append("bankDetails[bankName]", bankDetails.bankName || "");
      form.append("bankDetails[branchName]", bankDetails.branchName || "");
      form.append("bankDetails[accountNumber]", bankDetails.accountNumber || "");
      form.append("bankDetails[identifierCode]", bankDetails.identifierCode || "");
      form.append("bankDetails[tigoNumber]", bankDetails.tigoNumber || "");
    }

    return form;
  },
  vendorProfileUpdate: ({ profile, pendingCertificates }: IProfileUpdate) => {
    const form = new FormData();

    /** ================= BASIC DETAILS ================= */
    form.append("ownerName", profile.ownerName);
    form.append("name", profile.name);
    form.append("phone", profile.phone);
    form.append("phoneCountry", profile.phoneCountry);
    form.append("email", profile.email);
    form.append("about", profile.about || "");
    form.append("lat", String(profile.lat));
    form.append("lng", String(profile.lng));

    /** ================= BUSINESS DETAILS ================= */
    form.append("tinNumber", profile.tinNumber || "");
    form.append("vatNumber", profile.vatNumber || "");
    form.append("legalName", profile.legalName || "");
    form.append("tradeName", profile.tradeName || "");


    /** ================= ADDRESS ================= */
    // form.append("address", profile.address || "");
    form.append(
      "address[addressLine1]",
      profile.addressDetails?.addressLine1 || ""
    );
    form.append("address[city]", profile.addressDetails?.city || "");
    form.append("address[state]", profile.addressDetails?.state || "");
    form.append(
      "address[countryName]",
      profile.addressDetails?.countryName || ""
    );
    form.append(
      "address[postalCode]",
      profile.addressDetails?.postalCode || ""
    );

    /** ================= IMAGE ================= */
    if (profile.imageReader && profile.imageReader !== "") {
      form.append("image", profile.imageReader);
    }
    if (profile.removeImage) {
      form.append("removeImage", "true");
    }

    /** ================= CATEGORIES ================= */
    if (profile.categories?.length) {
      profile.categories.forEach((catVal: any, index: number) => {
        form.append(`categories[${index}]`, catVal.reference._id);
      });
    }

    /** ================= PAYMENT DETAILS ================= */
    form.append("paymentTerms", String(profile.paymentTerms || ""));
    form.append("paymentMode", String(profile.paymentMode || ""));
    form.append("creditLimit", String(profile.creditLimit || ""));
    form.append("leadDuration", String(profile.leadDuration || ""));
    form.append("currency", profile.currency || "");

    /** ================= WORKING DAYS ================= */
    if (profile.workingDays && Array.isArray(profile.workingDays)) {
      profile.workingDays.forEach((day: number, index: number) => {
        form.append(`workingDays[${index}]`, String(day));
      });
    }

    /** ================= TAX DETAILS ================= */
    if (profile.taxDetails) {
      form.append("taxDetails[type]", String(profile.taxDetails.type || ""));
      form.append("taxDetails[number]", profile.taxDetails.number || "");
    }

    /** ================= CONTRACT DETAILS ================= */
    form.append("annualContract", String(profile.hasAnnualContract || false));
    form.append("contractValidFrom", profile.contractValidFrom || "");
    form.append("contractValidTo", profile.contractValidTo || "");

    /** ================= CERTIFICATES (ONLY CHANGED) ================= */
    if (profile.certificates?.length) {
      const changedCertificates = profile.certificates.filter(
        (c: any) => c.isChanged === true
      );

      if (changedCertificates.length) {
        changedCertificates.forEach((val: any, index: number) => {
          form.append(`certificates[${index}][reference]`, val.reference._id);
          form.append(`certificates[${index}][expiry]`, val.expiry || "");

          if (val.imageReader) {
            form.append(`certificates[${index}][file]`, val.imageReader);
            form.append(`certificates[${index}][removeFile]`, "true");
          } else {
            form.append(`certificates[${index}][removeFile]`, "false");
          }
        });
      }
    }

    /** ================= BUSINESS TYPES ================= */
    if (profile.types?.length) {
      profile.types.forEach((businessVal: any, index: number) => {
        form.append(`types[${index}]`, businessVal.reference._id);
      });
    }

    /** ================= BANKS ================= */
    if (profile.banks?.length) {
      profile.banks.forEach((bank: any, index: number) => {
        if (bank.tigoNumber) {
          form.append(`banks[${index}][tigoNumber]`, bank.tigoNumber);
        }

        if (bank.bankName) {
          form.append(`banks[${index}][bankName]`, bank.bankName);
        }

        if (bank.branchName) {
          form.append(`banks[${index}][branchName]`, bank.branchName);
        }

        if (bank.accountNumber) {
          form.append(`banks[${index}][accountNumber]`, bank.accountNumber);
        }

        if (bank.bankType) {
          form.append(`banks[${index}][bankType]`, bank.bankType);
        }

        if (bank.identifierCode) {
          form.append(`banks[${index}][identifierCode]`, bank.identifierCode);
        }
      });
    }

    return form;
  },
};
